import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    listMatrixRecognitions,
    getMatrixRecognitionStats,
    getPrismHealth,
    PrismMatrixRow,
    PrismMatrixSummary,
    MatrixListParams,
} from '../../api/assets';
import { ApiError } from '../../api/request';
import { formatTime } from '../../utils/time';
import Pagination from '../../components/Pagination';
import SearchSelect from '../../components/SearchSelect';
import ImageLightbox from '../../components/ImageLightbox';
import { viewUrl } from '../../utils/imgUrl';
import { toast } from '../../utils/toast';
import { useRestoreQueryOnPop, useSyncQueryToUrl } from '../../utils/urlState';
import { useProcessTypeText } from '../../utils/dictOptions';
import {
    BarList, DataSourceBar, NoticeBar, ProcessThumb, RatingBadge, SegGroup, StatCard,
    SuccessRateBars, deviceColorMap,
} from './parts';
import PrismSyncBar from './PrismSyncBar';
import { usePrismReadLocal } from '../../utils/prismReadLocal';
import { MatrixStatusBadge } from './matrixParts';
import { deviceLabels, matrixBestText } from './matrixUtils';

// 数据资产·矩阵识别中心 (2026-10-09) — UI 对齐 studio-admin-console「矩阵识别」页.
// 与耗材识别中心一样**不做数据同步**: 后端直连 prism 的 matrix_tasks 实时查,
// 字段沿用 prism 原生 snake_case. 一行 = 用户在 Studio 上传一张参数矩阵测试图,
// 算法切格 + AI 逐格打分, 给出最佳格与推荐参数.

const PAGE_SIZE = 20;

function MatrixRecognitionList() {
    const history = useHistory();
    const location = useLocation();
    const craftText = useProcessTypeText();
    const initQ = new URLSearchParams(location.search);
    const [tab, setTab] = useState(initQ.get('tab') === 'table' ? 'table' : 'stats');
    const [status, setStatus] = useState(initQ.get('status') || '');
    const [rating, setRating] = useState(initQ.get('rating') || '');
    const [device, setDevice] = useState(initQ.get('device') || '');
    const [keyword, setKeyword] = useState(initQ.get('keyword') || '');
    const [keywordDraft, setKeywordDraft] = useState(initQ.get('keyword') || '');
    const [uid, setUid] = useState(initQ.get('uid') || '');
    const [uidDraft, setUidDraft] = useState(initQ.get('uid') || '');
    const [dateFrom, setDateFrom] = useState(initQ.get('date_from') || '');
    const [dateTo, setDateTo] = useState(initQ.get('date_to') || '');
    // 失败原因码: 只由失败原因图下钻带入 (筛选栏不放控件), 清空 / 切到非失败状态时一并清掉.
    const [errorCode, setErrorCode] = useState(initQ.get('error_code') || '');
    const [page, setPage] = useState(Number(initQ.get('page')) || 1);

    const [list, setList] = useState<PrismMatrixRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [summary, setSummary] = useState<PrismMatrixSummary | null>(null);
    const [health, setHealth] = useState<{ enabled: boolean; ok?: boolean; error?: string } | null>(null);
    const readLocal = usePrismReadLocal();
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);
    // 设备成功率排序 (纯展示, 不进 URL)
    const [rateDesc, setRateDesc] = useState(false);

    useSyncQueryToUrl('/assets/matrix-recognitions', {
        tab: tab === 'stats' ? '' : tab, status, rating, device, keyword, uid,
        date_from: dateFrom, date_to: dateTo, error_code: errorCode, page,
    });

    // 后退/前进: 按 URL 还原 tab 与筛选 (下钻会压一条历史记录, 后退应回到统计).
    useRestoreQueryOnPop((q) => {
        setTab(q.get('tab') === 'table' ? 'table' : 'stats');
        setStatus(q.get('status') || ''); setRating(q.get('rating') || ''); setDevice(q.get('device') || '');
        setKeyword(q.get('keyword') || ''); setKeywordDraft(q.get('keyword') || '');
        setUid(q.get('uid') || ''); setUidDraft(q.get('uid') || '');
        setDateFrom(q.get('date_from') || ''); setDateTo(q.get('date_to') || '');
        setErrorCode(q.get('error_code') || '');
        setPage(Number(q.get('page')) || 1);
    });

    // 统计与明细共用同一组筛选 (列表多一个分页).
    const filters: MatrixListParams = useMemo(() => ({
        status: status || undefined, rating: rating || undefined, device: device || undefined,
        keyword: keyword || undefined, uid: uid || undefined,
        date_from: dateFrom || undefined, date_to: dateTo || undefined,
        error_code: errorCode || undefined,
    }), [status, rating, device, keyword, uid, dateFrom, dateTo, errorCode]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listMatrixRecognitions({ ...filters, page, pageSize: PAGE_SIZE });
            setList(res.data || []);
            setTotal(res.total);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e));
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [filters, page]);

    const loadStats = useCallback(async () => {
        try {
            setSummary(await getMatrixRecognitionStats(filters));
        } catch (e) {
            toast(e instanceof ApiError ? e.message : String(e));
        }
    }, [filters]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadStats(); }, [loadStats]);
    // prism 同步跑完 (手动触发) → 列表与统计一起刷新.
    const onPrismSynced = useCallback(() => { load(); loadStats(); }, [load, loadStats]);
    useEffect(() => { getPrismHealth().then(setHealth).catch(() => undefined); }, []);

    const resetFilters = () => {
        setStatus(''); setRating(''); setDevice('');
        setKeyword(''); setKeywordDraft(''); setUid(''); setUidDraft('');
        setDateFrom(''); setDateTo(''); setErrorCode(''); setPage(1);
    };
    // 下钻: 整份替换筛选 + 切明细 tab. 直接 push 下钻后的地址, 浏览器后退就回到统计页.
    // (不能先 push 当前地址再 replace: hash history 对同一地址的 PUSH 会被忽略, 不产生记录.)
    const drill = (patch: { status?: string; device?: string; keyword?: string; rating?: string; error_code?: string }) => {
        const q = new URLSearchParams({ tab: 'table' });
        Object.entries(patch).forEach(([k, v]) => { if (v) q.set(k, v); });
        history.push(`${location.pathname}?${q.toString()}`);
        resetFilters();
        if (patch.status) setStatus(patch.status);
        if (patch.device) setDevice(patch.device);
        if (patch.rating) setRating(patch.rating);
        if (patch.keyword) { setKeyword(patch.keyword); setKeywordDraft(patch.keyword); }
        if (patch.error_code) setErrorCode(patch.error_code);
        setTab('table');
    };

    const k = summary?.kpi;
    const byDevice = useMemo(() => summary?.by_device || [], [summary]);
    // 机型码 → 展示名 (平台机型名优先, 对不上回退机型码; 重名补码).
    const devLabel = useMemo(() => deviceLabels(byDevice), [byDevice]);
    // 设备颜色按任务量降序固定, 两张设备图同一设备同色 (配色键 = 展示名, 与图表 label 一致).
    const colorMap = useMemo(() => deviceColorMap(byDevice.map((d) => devLabel[d.device])), [byDevice, devLabel]);
    // 设备选项带任务量; 选中的设备在当前筛选下可能已不在分布里, 补一项免得显示成空.
    const deviceOptions = useMemo(() => {
        const opts = byDevice.filter((d) => d.device && d.device !== '未知')
            // 名字 + 码一起进 label: 下拉的搜索按 label 过滤, 输机型名或机型码都能搜到.
            .map((d) => ({
                value: d.device,
                label: `${d.name ? `${d.name} · ${d.device}` : d.device}（${d.total.toLocaleString()}）`,
            }));
        if (device && !opts.some((o) => o.value === device)) opts.unshift({ value: device, label: device });
        return opts;
    }, [byDevice, device]);
    const selectedDeviceLabel = device ? (devLabel[device] || device) : '';
    // 失败原因码 → 中文 (取统计里的 label; 统计还没回来时先显示码).
    const errLabelOf = (code: string) => summary?.by_error.find((e) => e.code === code)?.label || code;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-5">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-[10px] font-extrabold tracking-[4px] uppercase text-slate-400 mb-1">Prism · Matrix</div>
                    <div className="text-xl font-extrabold text-slate-900 tracking-tight">矩阵识别</div>
                    <div className="text-xs text-slate-500 mt-1.5">
                        用户在 Studio 拍下参数矩阵测试图，AI 切格后逐格打分，给出最佳格与推荐参数。这里看任务量级、成功率、失败原因；点开任意一条看网格打分。
                    </div>
                </div>
                <div className="flex items-start gap-4 shrink-0">
                    {/* 读路径切到本库前仍直连 prism (DataSourceBar), 同步状态条同时显示回填进度 */}
                    {!readLocal && <DataSourceBar health={health} />}
                    <PrismSyncBar onSynced={onPrismSynced} />
                </div>
            </div>

            <div className="card px-4 py-3 space-y-2.5">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                    <SegGroup label="状态" value={status} onChange={(v) => { setStatus(v); if (v !== 'failed') setErrorCode(''); setPage(1); }}
                        options={[{ value: '', label: '全部' }, { value: 'done', label: '完成' },
                            { value: 'failed', label: '失败' }, { value: 'processing', label: '处理中' }]} />
                    <SegGroup label="评价" value={rating} onChange={(v) => { setRating(v); setPage(1); }}
                        options={[{ value: '', label: '全部' }, { value: 'good', label: '好评' },
                            { value: 'bad', label: '差评' }, { value: 'none', label: '无反馈' }]} />
                    <button type="button" className="ml-auto text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded"
                        onClick={resetFilters}>清空</button>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-2.5 border-t border-slate-50">
                    {/* 设备十几个且会增长: 可搜索下拉 (键入机型码过滤), 可清空回「全部设备」 */}
                    <SearchSelect className="w-48" clearable placeholder="全部设备"
                        value={device} valueLabel={selectedDeviceLabel}
                        options={deviceOptions}
                        onChange={(v) => { setDevice(v); setPage(1); }} />
                    <input className="inp" style={{ width: 200 }} placeholder="材料名 / 任务 ID，回车搜索" value={keywordDraft}
                        onChange={(e) => setKeywordDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setKeyword(keywordDraft.trim()); setPage(1); } }} />
                    <input className="inp" style={{ width: 120 }} placeholder="用户 UID…" value={uidDraft}
                        onChange={(e) => setUidDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setUid(uidDraft.trim()); setPage(1); } }} />
                    <input type="date" className="inp" style={{ width: 148 }} value={dateFrom}
                        onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
                    <span className="text-slate-300">~</span>
                    <input type="date" className="inp" style={{ width: 148 }} value={dateTo}
                        onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
                </div>
            </div>

            <div className="flex items-center gap-1">
                {[['stats', '统计'], ['table', '矩阵明细']].map(([key, label]) => (
                    <button key={key} type="button"
                        className={`px-[15px] py-1.5 rounded-[9px] text-[13px] font-semibold ${tab === key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                        onClick={() => setTab(key)}>
                        {label}
                    </button>
                ))}
            </div>

            {tab === 'stats' ? (
                <div className="space-y-4">
                    <div className="grid grid-cols-4 gap-4">
                        <StatCard label="矩阵任务量" value={(k?.total ?? 0).toLocaleString()}
                            sub={`${(k?.users ?? 0).toLocaleString()} 位用户`} />
                        <StatCard label="成功率" value={k ? `${k.success_rate}%` : '—'}
                            sub={`完成 ${(k?.done ?? 0).toLocaleString()}`} accent="text-emerald-600" />
                        <StatCard label="失败" value={(k?.failed ?? 0).toLocaleString()}
                            sub={`处理中 ${(k?.processing ?? 0).toLocaleString()}`} accent="text-rose-600" />
                        <StatCard label="用户反馈" value={((k?.good ?? 0) + (k?.bad ?? 0)).toLocaleString()}
                            sub={`好评 ${k?.good ?? 0} · 差评 ${k?.bad ?? 0}`} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <BarList title="设备分布" hint="按任务量" note="点任意一项 → 跳到矩阵明细"
                            rows={byDevice.map((d) => ({
                                label: devLabel[d.device], value: d.total, color: colorMap[devLabel[d.device]],
                                title: d.name ? `${d.name}（机型码 ${d.device}）` : `机型码 ${d.device}（平台设备库未收录）`,
                                onClick: d.device === '未知' ? undefined : () => drill({ device: d.device }),
                            }))} />
                        <div className="card p-4">
                            <div className="flex items-baseline gap-2 mb-3">
                                <div className="text-sm font-semibold text-slate-700">设备成功率</div>
                                <div className="text-[11px] text-slate-400 ml-auto">完成 / 任务量 · n 是任务量</div>
                                {/* 与材料加工页同款排序切换 */}
                                <SegGroup
                                    value={rateDesc ? 'desc' : 'asc'}
                                    options={[{ value: 'asc', label: '低→高' }, { value: 'desc', label: '高→低' }]}
                                    onChange={(v) => setRateDesc(v === 'desc')}
                                />
                            </div>
                            <SuccessRateBars colorMap={colorMap} desc={rateDesc}
                                rows={byDevice.map((d) => ({ label: devLabel[d.device], rate: d.success_rate, n: d.total }))} />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <BarList title="失败原因" labelWidth="w-64"
                            hint={k && k.failed > 0 ? `共 ${k.failed.toLocaleString()} 次失败` : undefined}
                            note="按 prism 错误码归类，悬停看错误码与一条原始错误信息。点任意一项 → 只看该原因的失败任务"
                            rows={(summary?.by_error || []).map((e) => ({
                                label: e.label || e.code, value: e.count, color: '#fb7185',
                                title: `${e.code}${e.sample ? `\n${e.sample}` : ''}`,
                                onClick: () => drill({ status: 'failed', error_code: e.code }),
                            }))} />
                        <BarList title="测试最多的材料 Top 12" labelWidth="w-44"
                            hint="按用户填写的材料名原文"
                            note="点任意一项 → 按该材料名搜索明细"
                            rows={(summary?.by_material || []).map((m) => ({
                                label: m.material, value: m.count,
                                share: m.count > 0 ? `成功 ${Math.round((m.done / m.count) * 100)}%` : undefined,
                                onClick: () => drill({ keyword: m.material }),
                            }))} />
                    </div>
                </div>
            ) : (
                <>
                    {(status || rating || device || keyword || uid || errorCode) && (
                        <NoticeBar>
                            当前筛选
                            {status && ` · 状态 ${status === 'done' ? '完成' : status === 'failed' ? '失败' : '处理中'}`}
                            {errorCode && ` · 失败原因「${errLabelOf(errorCode)}」`}
                            {rating && ` · 评价 ${rating === 'good' ? '好评' : rating === 'bad' ? '差评' : '无反馈'}`}
                            {device && ` · 设备 ${selectedDeviceLabel}`}
                            {keyword && ` · 搜索「${keyword}」`}
                            {uid && ` · 用户 ${uid}`}
                            <button type="button" className="ml-2 text-blue-600 hover:underline" onClick={resetFilters}>清空</button>
                        </NoticeBar>
                    )}
                    {error && <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>}
                    <div className="card overflow-x-auto">
                        <div className="flex items-center px-4 pt-3 pb-2">
                            <div className="text-sm font-semibold text-slate-700">矩阵明细</div>
                            <div className="ml-auto text-xs text-slate-400">共 {total.toLocaleString()} 条</div>
                        </div>
                        <table className="w-full text-[13px]" style={{ minWidth: 1000 }}>
                            <thead>
                                <tr className="text-left text-[10px] font-extrabold uppercase text-slate-400 border-b border-slate-100" style={{ letterSpacing: '1.5px', background: 'rgb(248 250 252 / 60%)' }}>
                                    <th className="px-3 py-2.5 whitespace-nowrap">图片</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" style={{ minWidth: 170 }}>材料</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">用户</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">设备</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="矩阵行×列 · 工艺 (取自任务入参, 2026-08 前的存量没有)">矩阵</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">最佳推荐</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="最佳格的 AI 评分 0—100">Score</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">评价</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">状态</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">时间</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={10} className="text-center text-slate-400 py-10">加载中…</td></tr>
                                ) : list.length === 0 ? (
                                    <tr><td colSpan={10} className="text-center text-slate-400 py-10">暂无数据</td></tr>
                                ) : (
                                    list.map((r) => (
                                        <tr key={r.task_id}
                                            className="border-b border-slate-50 hover:bg-blue-50/40 cursor-pointer"
                                            style={r.rating === 'bad' ? { boxShadow: 'inset 3px 0 0 #e11d48' } : undefined}
                                            onClick={() => history.push(`/assets/matrix-recognitions/${encodeURIComponent(r.task_id)}`)}>
                                            <td className="px-3 py-3">
                                                <ProcessThumb src={r.image_url || r.thumb_url} label="矩阵图" onZoom={setZoomSrc} />
                                            </td>
                                            <td className="px-3 py-3">
                                                {/* 源端材料名常为空 (Studio 只带了材料 id), 关联到平台材料时退回平台名 */}
                                                <div className="text-slate-800 break-words max-w-[240px]" title={r.material_name}
                                                    style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {r.material_name || r.material?.name?.zh || r.material?.name?.en || '—'}
                                                </div>
                                                {r.material ? (
                                                    <div className="text-[11px] font-mono text-brand-600">{r.material.xcode}</div>
                                                ) : r.is_local ? (
                                                    <div className="text-[11px] text-slate-400">本地素材</div>
                                                ) : null}
                                            </td>
                                            <td className="px-3 py-3 font-mono text-[11px] text-slate-500 truncate" title={r.uid || ''}>{r.uid || '—'}</td>
                                            <td className="px-3 py-3">
                                                <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px] whitespace-nowrap">
                                                    {r.device_name || r.device_model || '—'}
                                                </span>
                                                {r.device_name && <div className="text-[10px] font-mono text-slate-400 mt-0.5">{r.device_model}</div>}
                                            </td>
                                            <td className="px-3 py-3 text-[11px] text-slate-600 whitespace-nowrap">
                                                {r.n_rows && r.n_cols ? <span className="font-mono">{r.n_rows}×{r.n_cols}</span> : '—'}
                                                {r.process_type && <div className="text-slate-400">{craftText(r.process_type, r.process_type)}</div>}
                                            </td>
                                            <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap">{matrixBestText(r.best_position, r.recommended_parameters)}</td>
                                            <td className="px-3 py-3 font-mono tabular-nums text-slate-700">{r.best_position?.score ?? '—'}</td>
                                            <td className="px-3 py-3"><RatingBadge rating={r.rating} /></td>
                                            <td className="px-3 py-3" title={r.error_message || undefined}>
                                                <MatrixStatusBadge status={r.status} />
                                                {r.error_label && (
                                                    <div className="text-[11px] text-rose-500 mt-1 max-w-[180px] truncate">{r.error_label}</div>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 text-[11px] font-mono text-slate-500 whitespace-nowrap">{formatTime(r.created_at)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                        <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 text-xs text-slate-500">
                            <div>{total === 0 ? '0' : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)}`} / {total.toLocaleString()}</div>
                            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                        </div>
                    </div>
                </>
            )}

            <ImageLightbox src={viewUrl(zoomSrc)} onClose={() => setZoomSrc(null)} />
        </div>
    );
}

export default MatrixRecognitionList;
